import { Mail, Instagram, Youtube, Linkedin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  return (
    <div className="pt-32 md:pt-40">
      {/* HEADER */}
      <section className="pb-16 md:pb-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-6">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Contact
            </div>
            <h1 className="font-playfair text-5xl md:text-7xl lg:text-[7.5rem] font-semibold text-[#2C2C2C] leading-[0.95] tracking-tight max-w-5xl">
              Say <em className="text-[#8B1E2D]">hello</em>.
            </h1>
            <p className="mt-8 font-inter text-[#555555] text-lg md:text-xl leading-relaxed max-w-2xl">
              Reach out to collaborate, volunteer, or simply start a conversation. We'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-7">
            <form
              action="https://formspree.io/f/xpqbwvor"
              method="POST"
              className="space-y-8"
              data-testid="contact-form"
            >
              <div className="border-t border-black/15 pt-6">
                <label htmlFor="name" className="block font-inter text-[10px] tracking-[0.3em] uppercase text-[#E76F2E] mb-3">
                  01 · Your name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="How should we address you?"
                  className="w-full bg-transparent border-0 border-b border-black/20 focus:border-[#8B1E2D] outline-none py-3 font-playfair text-2xl md:text-3xl text-[#2C2C2C] placeholder:text-[#555555]/40 transition-colors"
                  data-testid="contact-input-name"
                />
              </div>

              <div className="border-t border-black/15 pt-6">
                <label htmlFor="email" className="block font-inter text-[10px] tracking-[0.3em] uppercase text-[#E76F2E] mb-3">
                  02 · Your email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full bg-transparent border-0 border-b border-black/20 focus:border-[#8B1E2D] outline-none py-3 font-playfair text-2xl md:text-3xl text-[#2C2C2C] placeholder:text-[#555555]/40 transition-colors"
                  data-testid="contact-input-email"
                />
              </div>

              <div className="border-t border-black/15 pt-6">
                <label htmlFor="message" className="block font-inter text-[10px] tracking-[0.3em] uppercase text-[#E76F2E] mb-3">
                  03 · Your message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us what's on your mind…"
                  className="w-full bg-transparent border-0 border-b border-black/20 focus:border-[#8B1E2D] outline-none py-3 font-inter text-lg text-[#2C2C2C] placeholder:text-[#555555]/40 transition-colors resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <div className="border-t border-black/15 pt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="font-inter text-xs text-[#555555] max-w-sm leading-relaxed">
                  We typically reply within a few days.
                </p>
                <button
                  type="submit"
                  className="btn-glow inline-flex items-center gap-2 bg-[#8B1E2D] text-white px-8 py-4 rounded-full font-inter text-sm tracking-wide hover:bg-[#721825] transition-colors"
                  data-testid="contact-submit"
                >
                  Send Message <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="md:col-span-5">
            <div className="sticky top-32 space-y-10">
              <div>
                <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">Direct email</div>
                <a
                  href="mailto:leoclubofmumbaicarterroad@gmail.com"
                  className="group inline-flex items-center gap-3 font-playfair text-2xl md:text-3xl text-[#2C2C2C] hover:text-[#8B1E2D] transition-colors"
                  data-testid="contact-email-link"
                >
                  <Mail size={22} className="text-[#8B1E2D]" />
                  leoclubofmumbaicarterroad@gmail.com
                </a>
              </div>

              <div>
                <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-5">Follow along</div>
                <div className="flex flex-col gap-3">
                  {[
                    { icon: Youtube, label: "YouTube", handle: "@leoclubofmcr", href: "https://www.youtube.com/@leoclubofmcr/videos", testId: "contact-social-youtube" },
                    { icon: Instagram, label: "Instagram", handle: "@leoclubofmcr", href: "https://instagram.com/leoclubofmcr", testId: "contact-social-instagram" },
                    { icon: Linkedin, label: "LinkedIn", handle: "Leo Club MCR", href: "https://www.linkedin.com/company/leo-club-of-mumbai-carter-road/about/", testId: "contact-social-linkedin" },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center justify-between border-t border-black/10 py-4 font-inter text-base text-[#2C2C2C] hover:text-[#8B1E2D] transition-colors"
                      data-testid={s.testId}
                    >
                      <span className="flex items-center gap-3">
                        <s.icon size={18} className="text-[#8B1E2D]" />
                        <span className="font-medium">{s.label}</span>
                        <span className="text-[#555555]">{s.handle}</span>
                      </span>
                      <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-[#F7F3EE] p-8">
                <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#8B1E2D] mb-3">Based in</div>
                <p className="font-playfair text-2xl text-[#2C2C2C] leading-snug">
                  Mumbai Carter Road
                </p>
                <p className="mt-3 font-inter text-sm text-[#555555] leading-relaxed">
                  Part of Lions Clubs International — a global network of service and leadership.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
